import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../ui/UIElements/Icon";
import { CampaignDatePicker } from "../../ui/Campaigns/CampaignDatePicker";
import { toast } from "sonner";
import {
  useCreateCampaign,
  useCampaignAudience,
} from "../../../hooks/useCampaigns";
import { useAiAgents } from "../../../hooks/useAiAgents";

const TEMPLATES = [
  {
    id: "newsletter",
    name: "Weekly Newsletter",
    subject: "This Week's Top Picks just for you! 📰",
    body: "Hi there! 👋\n\nWe have updated our menu with some exciting new additions that we think you will love. 🍔🍕🥗\n\nCome visit us this weekend! 🎉",
  },
  {
    id: "sale",
    name: "Flash Sale Alert",
    subject: "FLASH SALE: 50% OFF Everything! ⚡",
    body: "Hurry! ⏰ For the next 24 hours only, get 50% off all combos.\n\n🔥 Don't miss out on this exclusive offer! 🔥",
  },
  {
    id: "welcome",
    name: "Welcome Series",
    subject: "Welcome to the Finitless Family! ❤️",
    body: "We are thrilled to have you with us! 🎊\n\nAs a new member, you get access to exclusive deals and priority delivery. 🚀\n\nEnjoy your first order! 🛍️",
  },
];

const getAgentUrl = (agent) => {
  if (!agent?.site) return null;
  const site = agent.site;
  if (site?.domain && site?.ssl_status === "issued") {
    return `https://${site.domain}`;
  }
  if (site?.temporal_domain) {
    return `https://${site.temporal_domain}.finitless.ai`;
  }
  return null;
};

const PRESET_COLORS = ["#8b5cf6", "#000000", "#10b981", "#ef4444", "#f59e0b"];

const RULE_LIST = [
  {
    key: "orders_count",
    title: "Order History",
    description: "Target customers based on purchase count",
    icon: "Users",
  },
  {
    key: "user_created_at",
    title: "Registration Date",
    description: "Target new or long-time users",
    icon: "UserPlus",
  },
  {
    key: "user_last_open_cart_at",
    title: "Open Carts",
    description: "Target users with items left in cart",
    icon: "ShoppingBag",
  },
];

const CreateCampaignPage = () => {
  const { t } = useTranslation("campaigns");
  const navigate = useNavigate();
  const createCampaignMutation = useCreateCampaign();
  const { data: agentsData } = useAiAgents({
    enabledFilter: "enabled",
    pageSize: 50,
  });

  const destinationUrls = useMemo(() => {
    const urls = [];
    if (agentsData?.agents) {
      agentsData.agents.forEach((agent) => {
        const url = getAgentUrl(agent);
        if (url) {
          urls.push(url);
        }
      });
    }
    return urls;
  }, [agentsData]);

  const [campaignName, setCampaignName] = useState("");
  const [audienceType, setAudienceType] = useState("fixed");
  const [isEnabled, setIsEnabled] = useState(true);
  const [isASAP, setIsASAP] = useState(true);
  const [scheduledAt, setScheduledAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [rules, setRules] = useState({
    orders_count: { active: false, value: "1", condition: ">" },
    user_created_at: {
      active: false,
      days: "30",
      date_from: "",
      date_to: "",
      date_condition: "<",
    },
    user_last_open_cart_at: {
      active: false,
      days: "7",
      date_from: "",
      date_to: "",
      date_condition: "<",
    },
  });
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [targetUrl, setTargetUrl] = useState("");
  const [buttonText, setButtonText] = useState(t("createPage.preview.shopNow"));
  const [buttonColor, setButtonColor] = useState("#8b5cf6");

  useEffect(() => {
    if (destinationUrls.length > 0 && !targetUrl) {
      setTargetUrl(destinationUrls[0]);
    }
  }, [destinationUrls, targetUrl]);

  useEffect(() => {
    if (TEMPLATES.length > 0 && !selectedTemplate) {
      applyTemplate(TEMPLATES[0].id);
    }
  }, []);

  const getDaysAgoDate = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(date.getDate()).padStart(2, "0")} 00:00:00`;
  };

  const formatDateForApi = (dateStr) => `${dateStr} 00:00:00`;

  const conditions = useMemo(() => {
    const result = [];
    if (rules.orders_count.active && rules.orders_count.value) {
      result.push({
        type: "orders_count",
        condition: rules.orders_count.condition,
        value: parseInt(rules.orders_count.value) || 0,
      });
    }
    if (rules.user_created_at.active) {
      if (rules.user_created_at.date_condition === "><") {
        if (rules.user_created_at.date_from && rules.user_created_at.date_to) {
          result.push({
            type: "user_created_at",
            date_condition: "><",
            date: formatDateForApi(rules.user_created_at.date_from),
            max_date: formatDateForApi(rules.user_created_at.date_to),
          });
        }
      } else if (rules.user_created_at.days) {
        result.push({
          type: "user_created_at",
          date_condition: rules.user_created_at.date_condition,
          date: getDaysAgoDate(parseInt(rules.user_created_at.days) || 0),
        });
      }
    }
    if (rules.user_last_open_cart_at.active) {
      if (rules.user_last_open_cart_at.date_condition === "><") {
        if (
          rules.user_last_open_cart_at.date_from &&
          rules.user_last_open_cart_at.date_to
        ) {
          result.push({
            type: "user_last_open_cart_at",
            date_condition: "><",
            date: formatDateForApi(rules.user_last_open_cart_at.date_from),
            max_date: formatDateForApi(rules.user_last_open_cart_at.date_to),
          });
        }
      } else if (rules.user_last_open_cart_at.days) {
        result.push({
          type: "user_last_open_cart_at",
          date_condition: rules.user_last_open_cart_at.date_condition,
          date: getDaysAgoDate(
            parseInt(rules.user_last_open_cart_at.days) || 0,
          ),
        });
      }
    }
    return result;
  }, [rules]);

  const { data: audienceData, isLoading: isLoadingAudience } =
    useCampaignAudience(conditions, "email");

  const toggleRule = (key) =>
    setRules((prev) => ({
      ...prev,
      [key]: { ...prev[key], active: !prev[key].active },
    }));

  const applyTemplate = (templateId) => {
    const template = TEMPLATES.find((t) => t.id === templateId);
    if (template) {
      setSubject(template.subject);
      setBody(template.body);
      setSelectedTemplate(templateId);
    }
  };

  const handleClose = () => navigate("/campaigns");

  const generateHtmlBody = () => {
    const paragraphs = body.split("\n\n").filter((p) => p.trim());
    const htmlParagraphs = paragraphs
      .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
      .join("");
    const trackingUrl = `https://api.orderingplus.com/v400/en/luisv4plus/helpers/emails/click/mkt/{{tracking_id}}?next=${encodeURIComponent(targetUrl)}`;
    const buttonHtml = `<p style="text-align: center; margin-top: 20px;"><a href='${trackingUrl}' target='_blank'><button style='font-size: 14px; color: white; padding: 12px 24px; cursor: pointer; border-radius: 25px; background: ${buttonColor}; border: none;'>${buttonText}</button></a></p>`;
    return htmlParagraphs + buttonHtml;
  };

  const buildCampaignData = (status) => ({
    name: campaignName,
    contact_type: "email",
    audience_type: audienceType,
    enabled: isEnabled,
    status,
    conditions: JSON.stringify(conditions),
    contact_data: JSON.stringify({ title: subject, body: generateHtmlBody() }),
    ...(audienceType === "fixed" &&
      !isASAP &&
      scheduledAt && { scheduled_at: `${scheduledAt} 00:00:00` }),
    ...(audienceType === "dynamic" && endAt && { end_at: `${endAt} 00:00:00` }),
  });

  const handleSubmit = async () => {
    if (!campaignName.trim()) {
      toast.error(t("createPage.validation.campaignNameRequired"));
      return;
    }
    if (conditions.length === 0) {
      toast.error(t("createPage.validation.audienceRuleRequired"));
      return;
    }
    if (!subject.trim()) {
      toast.error(t("createPage.validation.subjectRequired"));
      return;
    }
    if (!body.trim()) {
      toast.error(t("createPage.validation.messageBodyRequired"));
      return;
    }
    try {
      const status =
        audienceType === "fixed"
          ? isASAP
            ? "pending"
            : "scheduled"
          : "pending";
      await createCampaignMutation.mutateAsync(buildCampaignData(status));
      toast.success(t("createPage.toasts.campaignCreated"));
      navigate("/campaigns");
    } catch (error) {
      toast.error(t("createPage.toasts.createFailed"), {
        description: error.message,
      });
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Sticky Header */}
      <div className="bg-background sticky -top-1 z-40 -mt-2 mb-6 border-b border-gray-800 pt-2 pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleClose}
              className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-800 hover:text-white"
            >
              <Icon name="ArrowLeft" className="h-5 w-5" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-white">
                {t("createPage.header.title")}
              </h2>
              <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                <span className="font-medium text-blue-400">
                  {t("createPage.header.steps.setup")}
                </span>
                <Icon name="ChevronRight" className="h-3 w-3" />
                <span className="font-medium text-blue-400">
                  {t("createPage.header.steps.audience")}
                </span>
                <Icon name="ChevronRight" className="h-3 w-3" />
                <span className="font-medium text-blue-400">
                  {t("createPage.header.steps.content")}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={handleSubmit}
            disabled={createCampaignMutation.isPending}
            className="ml-0 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-2 text-sm font-medium text-white transition-all hover:bg-blue-700 disabled:opacity-50 sm:ml-auto sm:w-auto"
          >
            {createCampaignMutation.isPending && (
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
            )}
            {t("actions.createCampaign")}
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-8 xl:flex-row">
        {/* Left Column - Scrollable */}
        <div className="flex-1 space-y-8 overflow-y-auto pb-10 xl:pr-2">
          {/* Section 1: General */}
          <section>
            <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-white">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/20 text-sm font-bold text-blue-400">
                1
              </span>
              {t("createPage.sections.generalDetails")}
            </h3>
            <div className="space-y-5 rounded-xl border border-gray-800 bg-gray-900 p-6">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-400">
                  {t("createPage.fields.campaignName.label")}
                </label>
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder={t("createPage.fields.campaignName.placeholder")}
                  className="w-full rounded-lg border border-gray-700 bg-gray-950 px-4 py-2.5 text-white placeholder-gray-600 outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="mb-3 block text-sm font-medium text-gray-400">
                  {t("createPage.fields.communicationChannel.label")}
                </label>
                <div className="flex items-center gap-4 rounded-xl border border-gray-700 bg-gray-950 p-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-600/10 text-blue-500">
                    <Icon name="Mail" className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white">
                      {t(
                        "createPage.fields.communicationChannel.emailCampaign",
                      )}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {t("createPage.fields.communicationChannel.onlyChannel")}
                    </p>
                  </div>
                  <div className="ml-auto">
                    <span className="rounded-full border border-blue-500/30 bg-blue-500/20 px-3 py-1 text-xs font-bold text-blue-400">
                      {t("createPage.fields.communicationChannel.selected")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Audience */}
          <section>
            <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-white">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/20 text-sm font-bold text-blue-400">
                2
              </span>
              {t("createPage.sections.defineAudience")}
            </h3>
            <div className="rounded-xl border border-gray-800 bg-gray-900 p-6">
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-gray-400">
                  {t("createPage.audience.subtitle")}
                </p>
                {conditions.length > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-400">
                      {t("createPage.audience.reaching")}
                    </span>
                    {isLoadingAudience ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-500"></div>
                    ) : (
                      <span className="font-medium text-white">
                        {audienceData?.pagination?.total || 0}{" "}
                        {t("createPage.audience.people")}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-3">
                {RULE_LIST.map((rule) => (
                  <div
                    key={rule.key}
                    className={`rounded-lg border transition-all ${
                      rules[rule.key]?.active
                        ? "border-blue-500 bg-blue-500/5"
                        : "border-gray-700 bg-gray-950/50"
                    }`}
                  >
                    <button
                      onClick={() => toggleRule(rule.key)}
                      className="flex w-full items-center p-4 text-left"
                    >
                      <div
                        className={`mr-3 flex h-5 w-5 items-center justify-center rounded border transition-colors ${
                          rules[rule.key]?.active
                            ? "border-blue-500 bg-blue-500"
                            : "border-gray-600"
                        }`}
                      >
                        {rules[rule.key]?.active && (
                          <Icon
                            name={rule.icon}
                            className="h-3 w-3 text-white"
                          />
                        )}
                      </div>
                      <div className="flex-1">
                        <span
                          className={`font-medium ${
                            rules[rule.key]?.active
                              ? "text-white"
                              : "text-gray-400"
                          }`}
                        >
                          {rule.title}
                        </span>
                        <p className="text-xs text-gray-500">
                          {rule.description}
                        </p>
                      </div>
                    </button>
                    {rule.key === "orders_count" &&
                      rules.orders_count.active && (
                        <div className="flex items-center gap-3 px-4 pb-4 pl-12">
                          <select
                            value={rules.orders_count.condition}
                            onChange={(e) =>
                              setRules({
                                ...rules,
                                orders_count: {
                                  ...rules.orders_count,
                                  condition: e.target.value,
                                },
                              })
                            }
                            className="custom-select rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          >
                            <option value=">">
                              {t("createPage.audience.conditions.moreThan")}
                            </option>
                            <option value="<">
                              {t("createPage.audience.conditions.lessThan")}
                            </option>
                            <option value="=">
                              {t("createPage.audience.conditions.exactly")}
                            </option>
                          </select>
                          <input
                            type="text"
                            value={rules.orders_count.value}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (
                                val === "" ||
                                (/^\d+$/.test(val) && parseInt(val) <= 999)
                              )
                                setRules({
                                  ...rules,
                                  orders_count: {
                                    ...rules.orders_count,
                                    value: val,
                                  },
                                });
                            }}
                            className="w-20 rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            maxLength={3}
                          />
                          <span className="text-sm text-gray-400">
                            {t("createPage.audience.conditions.ordersPlaced")}
                          </span>
                        </div>
                      )}
                    {rule.key === "user_created_at" &&
                      rules.user_created_at.active && (
                        <div className="flex flex-wrap items-center gap-3 px-4 pb-4 pl-12">
                          <span className="text-sm text-gray-400">
                            {t("createPage.audience.conditions.registered")}
                          </span>
                          <select
                            value={rules.user_created_at.date_condition}
                            onChange={(e) =>
                              setRules({
                                ...rules,
                                user_created_at: {
                                  ...rules.user_created_at,
                                  date_condition: e.target.value,
                                },
                              })
                            }
                            className="custom-select rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          >
                            <option value="<">
                              {t("createPage.audience.conditions.lessThan")}
                            </option>
                            <option value=">">
                              {t("createPage.audience.conditions.moreThan")}
                            </option>
                            <option value="><">
                              {t("createPage.audience.conditions.betweenDates")}
                            </option>
                          </select>
                          {rules.user_created_at.date_condition === "><" ? (
                            <>
                              <CampaignDatePicker
                                value={rules.user_created_at.date_from}
                                onChange={(val) =>
                                  setRules({
                                    ...rules,
                                    user_created_at: {
                                      ...rules.user_created_at,
                                      date_from: val,
                                    },
                                  })
                                }
                                placeholder={t(
                                  "createPage.audience.conditions.from",
                                )}
                              />
                              <span className="text-sm text-gray-400">
                                {t("createPage.audience.conditions.and")}
                              </span>
                              <CampaignDatePicker
                                value={rules.user_created_at.date_to}
                                onChange={(val) =>
                                  setRules({
                                    ...rules,
                                    user_created_at: {
                                      ...rules.user_created_at,
                                      date_to: val,
                                    },
                                  })
                                }
                                placeholder={t(
                                  "createPage.audience.conditions.to",
                                )}
                              />
                            </>
                          ) : (
                            <>
                              <input
                                type="text"
                                value={rules.user_created_at.days}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (
                                    val === "" ||
                                    (/^\d+$/.test(val) && parseInt(val) <= 999)
                                  )
                                    setRules({
                                      ...rules,
                                      user_created_at: {
                                        ...rules.user_created_at,
                                        days: val,
                                      },
                                    });
                                }}
                                className="w-20 rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                maxLength={3}
                              />
                              <span className="text-sm text-gray-400">
                                {t("createPage.audience.conditions.daysAgo")}
                              </span>
                            </>
                          )}
                        </div>
                      )}
                    {rule.key === "user_last_open_cart_at" &&
                      rules.user_last_open_cart_at.active && (
                        <div className="flex flex-wrap items-center gap-3 px-4 pb-4 pl-12">
                          <span className="text-sm text-gray-400">
                            {t("createPage.audience.conditions.cartOpened")}
                          </span>
                          <select
                            value={rules.user_last_open_cart_at.date_condition}
                            onChange={(e) =>
                              setRules({
                                ...rules,
                                user_last_open_cart_at: {
                                  ...rules.user_last_open_cart_at,
                                  date_condition: e.target.value,
                                },
                              })
                            }
                            className="custom-select rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          >
                            <option value="<">
                              {t("createPage.audience.conditions.lessThan")}
                            </option>
                            <option value=">">
                              {t("createPage.audience.conditions.moreThan")}
                            </option>
                            <option value="><">
                              {t("createPage.audience.conditions.betweenDates")}
                            </option>
                          </select>
                          {rules.user_last_open_cart_at.date_condition ===
                          "><" ? (
                            <>
                              <CampaignDatePicker
                                value={rules.user_last_open_cart_at.date_from}
                                onChange={(val) =>
                                  setRules({
                                    ...rules,
                                    user_last_open_cart_at: {
                                      ...rules.user_last_open_cart_at,
                                      date_from: val,
                                    },
                                  })
                                }
                                placeholder={t(
                                  "createPage.audience.conditions.from",
                                )}
                              />
                              <span className="text-sm text-gray-400">
                                {t("createPage.audience.conditions.and")}
                              </span>
                              <CampaignDatePicker
                                value={rules.user_last_open_cart_at.date_to}
                                onChange={(val) =>
                                  setRules({
                                    ...rules,
                                    user_last_open_cart_at: {
                                      ...rules.user_last_open_cart_at,
                                      date_to: val,
                                    },
                                  })
                                }
                                placeholder={t(
                                  "createPage.audience.conditions.to",
                                )}
                              />
                            </>
                          ) : (
                            <>
                              <input
                                type="text"
                                value={rules.user_last_open_cart_at.days}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (
                                    val === "" ||
                                    (/^\d+$/.test(val) && parseInt(val) <= 999)
                                  )
                                    setRules({
                                      ...rules,
                                      user_last_open_cart_at: {
                                        ...rules.user_last_open_cart_at,
                                        days: val,
                                      },
                                    });
                                }}
                                className="w-20 rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                maxLength={3}
                              />
                              <span className="text-sm text-gray-400">
                                {t("createPage.audience.conditions.daysAgo")}
                              </span>
                            </>
                          )}
                        </div>
                      )}
                  </div>
                ))}
              </div>
              {/* Audience Type */}
              <div className="mt-6 border-t border-gray-800 pt-6">
                <label className="mb-3 block text-sm font-medium text-gray-400">
                  {t("createPage.audience.audienceType")}
                </label>
                <div className="space-y-3">
                  <div
                    onClick={() => setAudienceType("fixed")}
                    className={`cursor-pointer rounded-lg border p-4 transition-all ${
                      audienceType === "fixed"
                        ? "border-blue-500 bg-blue-500/5"
                        : "border-gray-700 bg-gray-950/50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                          audienceType === "fixed"
                            ? "border-blue-500"
                            : "border-gray-600"
                        }`}
                      >
                        {audienceType === "fixed" && (
                          <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                        )}
                      </div>
                      <div>
                        <span className="font-medium text-white">Fixed</span>
                        <p className="text-xs text-gray-500">
                          {t("createPage.audience.fixedDescription")}
                        </p>
                      </div>
                    </div>
                    {audienceType === "fixed" && (
                      <div className="mt-4 ml-7 space-y-3">
                        <p className="text-sm font-medium text-gray-400">
                          {t("createPage.audience.deliverySchedule")}
                        </p>
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsASAP(true);
                          }}
                          className="flex cursor-pointer items-center gap-2"
                        >
                          <div
                            className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                              isASAP ? "border-blue-500" : "border-gray-600"
                            }`}
                          >
                            {isASAP && (
                              <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                            )}
                          </div>
                          <span className="text-sm text-gray-300">
                            {t("createPage.audience.sendAsap")}
                          </span>
                        </div>
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsASAP(false);
                          }}
                          className="flex cursor-pointer items-center gap-2"
                        >
                          <div
                            className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                              !isASAP ? "border-blue-500" : "border-gray-600"
                            }`}
                          >
                            {!isASAP && (
                              <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                            )}
                          </div>
                          <span className="text-sm text-gray-300">
                            {t("createPage.audience.scheduleForLater")}
                          </span>
                        </div>
                        {!isASAP && (
                          <div
                            className="ml-6"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <CampaignDatePicker
                              value={scheduledAt}
                              onChange={setScheduledAt}
                              placeholder={t(
                                "createPage.audience.conditions.selectDate",
                              )}
                              minDate={new Date()}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div
                    onClick={() => setAudienceType("dynamic")}
                    className={`cursor-pointer rounded-lg border p-4 transition-all ${
                      audienceType === "dynamic"
                        ? "border-blue-500 bg-blue-500/5"
                        : "border-gray-700 bg-gray-950/50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                          audienceType === "dynamic"
                            ? "border-blue-500"
                            : "border-gray-600"
                        }`}
                      >
                        {audienceType === "dynamic" && (
                          <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                        )}
                      </div>
                      <div>
                        <span className="font-medium text-white">Dynamic</span>
                        <p className="text-xs text-gray-500">
                          {t("createPage.audience.dynamicDescription")}
                        </p>
                      </div>
                    </div>
                    {audienceType === "dynamic" && (
                      <div className="mt-4 ml-7 space-y-3">
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-400">
                            {t("createPage.audience.enableCampaign")}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsEnabled(!isEnabled);
                            }}
                            className={`relative h-5 w-10 rounded-full transition-colors ${
                              isEnabled ? "bg-blue-500" : "bg-gray-700"
                            }`}
                          >
                            <div
                              className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
                                isEnabled ? "translate-x-5" : "translate-x-0.5"
                              }`}
                            ></div>
                          </button>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-400">
                            {t("createPage.audience.endDate")}
                          </span>
                          <div onClick={(e) => e.stopPropagation()}>
                            <CampaignDatePicker
                              value={endAt}
                              onChange={setEndAt}
                              placeholder={t(
                                "createPage.audience.conditions.selectDate",
                              )}
                              minDate={new Date()}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Content */}
          <section>
            <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-white">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/20 text-sm font-bold text-blue-400">
                3
              </span>
              {t("createPage.sections.messageContent")}
            </h3>
            <div className="space-y-6 rounded-xl border border-gray-800 bg-gray-900 p-6">
              <div>
                <label className="mb-3 block flex items-center gap-2 text-sm font-medium text-gray-400">
                  <Icon name="LayoutTemplate" className="h-4 w-4" />
                  {t("createPage.content.chooseTemplate")}
                </label>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => applyTemplate(template.id)}
                      className={`relative rounded-lg border p-3 text-left transition-all ${
                        selectedTemplate === template.id
                          ? "border-blue-500 bg-blue-600/10 shadow-md shadow-blue-500/10"
                          : "border-gray-700 bg-gray-950 hover:border-gray-600 hover:bg-gray-800"
                      }`}
                    >
                      <h4
                        className={`mb-1 text-sm font-semibold ${
                          selectedTemplate === template.id
                            ? "text-white"
                            : "text-gray-300"
                        }`}
                      >
                        {template.name}
                      </h4>
                      <p className="line-clamp-2 text-[10px] text-gray-500">
                        {template.subject}
                      </p>
                      {selectedTemplate === template.id && (
                        <div className="absolute top-2 right-2 flex h-4 w-4 items-center justify-center rounded-full bg-blue-500">
                          <Icon
                            name="Check"
                            className="h-2.5 w-2.5 text-white"
                          />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              <div className="h-px w-full bg-gray-700"></div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-400">
                  {t("createPage.content.subjectLine")}
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t("createPage.content.subjectPlaceholder")}
                  className="w-full rounded-lg border border-gray-700 bg-gray-950 px-4 py-2.5 text-white placeholder-gray-600 outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-400">
                  {t("createPage.content.messageBody")}
                </label>
                <div className="relative">
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={8}
                    placeholder={t("createPage.content.messagePlaceholder")}
                    className="w-full resize-none rounded-lg border border-gray-700 bg-gray-950 p-4 leading-relaxed text-white placeholder-gray-600 outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="absolute right-3 bottom-3 text-xs text-gray-500">
                    {body.length} characters
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block flex items-center gap-2 text-sm font-medium text-gray-400">
                    <Icon name="Globe" className="h-3.5 w-3.5" />
                    {t("createPage.content.buttonDestination")}
                  </label>
                  <select
                    value={targetUrl}
                    onChange={(e) => setTargetUrl(e.target.value)}
                    className="custom-select w-full cursor-pointer rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    {destinationUrls.map((url) => (
                      <option key={url} value={url}>
                        {url}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block flex items-center gap-2 text-sm font-medium text-gray-400">
                    <Icon name="MousePointer" className="h-3.5 w-3.5" />
                    {t("createPage.content.callToActionStyle")}
                  </label>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={buttonText}
                      onChange={(e) => setButtonText(e.target.value)}
                      placeholder={t(
                        "createPage.content.buttonTextPlaceholder",
                      )}
                      className="w-full rounded-lg border border-gray-700 bg-gray-950 px-4 py-2.5 text-white placeholder-gray-600 outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex items-center gap-2">
                      <span className="mr-1 text-xs text-gray-500">
                        {t("createPage.content.color")}
                      </span>
                      <div className="flex gap-2">
                        {PRESET_COLORS.map((color) => (
                          <button
                            key={color}
                            onClick={() => setButtonColor(color)}
                            className={`h-8 w-8 rounded-full border-2 transition-all ${
                              buttonColor === color
                                ? "scale-110 border-white"
                                : "border-transparent hover:scale-105"
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                        <div className="relative h-8 w-8 cursor-pointer overflow-hidden rounded-full border-2 border-gray-600 transition-transform hover:scale-105">
                          <input
                            type="color"
                            value={buttonColor}
                            onChange={(e) => setButtonColor(e.target.value)}
                            className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                          />
                          <div className="h-full w-full bg-gradient-to-r from-red-500 via-blue-500 to-blue-500"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Right Column: Device Preview - Fixed position on desktop, below content on mobile */}
        <div className="w-full shrink-0 pt-2 xl:sticky xl:top-16 xl:w-[350px] xl:self-start">
          <h3 className="mb-4 flex items-center justify-center gap-2 text-sm font-semibold tracking-wider text-gray-400 uppercase xl:justify-start">
            <Icon name="Smartphone" className="h-4 w-4" />{" "}
            {t("createPage.preview.devicePreview")}
          </h3>
          <div className="mx-auto max-w-[350px] rounded-[2.5rem] border-4 border-gray-800 bg-gray-950 p-2 xl:mx-0">
            <div className="relative">
              <div className="absolute top-0 left-1/2 z-10 flex h-6 w-24 -translate-x-1/2 transform items-center justify-center gap-1 rounded-b-xl bg-gray-950">
                <div className="h-2 w-2 rounded-full bg-gray-800"></div>
                <div className="h-2 w-2 rounded-full bg-gray-800"></div>
              </div>
              <div className="overflow-hidden rounded-[2rem] bg-white">
                <div className="flex items-center justify-between bg-gray-100 px-4 py-2 text-xs text-gray-600">
                  <span className="font-bold">9:41</span>
                  <div className="flex items-center gap-1">
                    <div className="h-2 w-4 rounded-sm bg-gray-400"></div>
                    <div className="h-2 w-4 rounded-sm bg-gray-400"></div>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-blue-600 px-4 py-3">
                  <Icon name="ArrowLeft" className="h-5 w-5 text-white" />
                  <span className="font-medium text-white">
                    {t("createPage.preview.inbox")}
                  </span>
                  <Icon name="Search" className="h-5 w-5 text-white" />
                </div>
                <div className="min-h-[550px] p-4">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                      <span className="text-sm font-bold text-blue-600">F</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        Finitless
                      </p>
                      <p className="text-xs text-gray-500">
                        {t("createPage.preview.toMe")}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400">
                      {t("createPage.preview.now")}
                    </span>
                  </div>
                  <h3 className="mb-3 text-sm leading-tight font-semibold text-gray-900">
                    {subject || t("createPage.preview.subjectFallback")}
                  </h3>
                  <div className="text-xs leading-relaxed whitespace-pre-line text-gray-600">
                    {body || t("createPage.preview.messageFallback")}
                  </div>
                  <div className="mt-6 border-t border-gray-100 pt-4 text-center">
                    <button
                      onClick={() =>
                        targetUrl && window.open(targetUrl, "_blank")
                      }
                      className="inline-block max-w-[200px] cursor-pointer rounded-full px-8 py-3 text-xs font-bold text-white shadow-lg transition-opacity hover:opacity-90"
                      style={{ backgroundColor: buttonColor }}
                    >
                      <span
                        className="block overflow-hidden"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          lineHeight: "1.4",
                        }}
                      >
                        {buttonText || t("createPage.preview.shopNow")}
                      </span>
                    </button>
                    <p className="mt-4 text-[10px] text-gray-400">
                      {t("createPage.preview.buttonLinksTo")}{" "}
                      <span className="text-blue-600">{targetUrl}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCampaignPage;
